'use client';

/**
 * MergeStrategySelector Component
 *
 * Select merge strategy for a specific field during record merging.
 * Part of the identity resolution component suite.
 */

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef, useCallback, useState } from 'react';
import { baseColors, getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { FieldDefinition } from '../../types/entityFramework';
import type { MergeStrategyType } from '../../types/identityResolution';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface MergeStrategySelectorProps {
  /** Field definition */
  field: FieldDefinition;
  /** Primary record value */
  primaryValue: unknown;
  /** Secondary record value */
  secondaryValue: unknown;
  /** Current strategy */
  strategy: MergeStrategyType;
  /** Custom value (when strategy is CUSTOM) */
  customValue?: unknown;
  /** Strategy change handler */
  onStrategyChange: (strategy: MergeStrategyType, customValue?: unknown) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
}

// ============================================================================
// Constants
// ============================================================================

const STRATEGY_OPTIONS: {
  value: MergeStrategyType;
  label: string;
  icon: string;
}[] = [
  { value: 'KEEP_PRIMARY', label: 'Primary', icon: '\u2190' }, // left arrow
  { value: 'KEEP_SECONDARY', label: 'Secondary', icon: '\u2192' }, // right arrow
  { value: 'MERGE', label: 'Merge', icon: '\u2194' }, // left-right arrow
  { value: 'CUSTOM', label: 'Custom', icon: '\u270E' }, // pencil
];

// ============================================================================
// Helper Functions
// ============================================================================

function formatValue(value: unknown, fieldType: string): string {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (fieldType) {
    case 'DATE':
    case 'DATETIME':
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      if (typeof value === 'string') {
        return new Date(value).toLocaleDateString();
      }
      return String(value);

    case 'BOOLEAN':
      return value ? 'Yes' : 'No';

    case 'CURRENCY':
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      }
      return String(value);

    case 'NUMBER':
    case 'PERCENTAGE':
      if (typeof value === 'number') {
        return fieldType === 'PERCENTAGE' ? `${value}%` : value.toLocaleString();
      }
      return String(value);

    case 'ADDRESS':
      if (typeof value === 'object' && value !== null) {
        const addr = value as Record<string, string>;
        return [addr['street1'], addr['city'], addr['state'], addr['postalCode']].filter(Boolean).join(', ');
      }
      return String(value);

    case 'MULTI_SELECT':
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return String(value);

    default:
      return String(value);
  }
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: theme.spacing(2),
    borderRadius: 12,
    background: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${glass.border}`,
    transition: transitionPresets.all,
  };
});

const StyledFieldHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),

  '& .field-label': {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  '& .field-type': {
    fontSize: '0.6875rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.disabled,
    padding: '2px 8px',
    borderRadius: 4,
    background: theme.palette.action.hover,
  },
}));

const StyledValuesRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

interface StyledValueCardProps {
  $isDark: boolean;
  $selected: boolean;
  $side: 'primary' | 'secondary';
  $accentColor?: string;
}

const StyledValueCard = styled(ButtonBase, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledValueCardProps>(({ theme, $isDark, $selected, $side, $accentColor }) => {
  const sideColor =
    $side === 'primary' ? baseColors.primary[$isDark ? 400 : 500] : baseColors.secondary[$isDark ? 400 : 500];
  const color = $accentColor || sideColor;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    textAlign: 'left',
    background: $selected ? alpha(color, 0.12) : $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: `2px solid ${$selected ? alpha(color, 0.4) : 'transparent'}`,
    transition: transitionPresets.all,
    cursor: 'pointer',

    '&:hover': {
      background: alpha(color, 0.08),
      border: `2px solid ${alpha(color, 0.25)}`,
    },

    '& .value-label': {
      fontSize: '0.6875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: $selected ? color : theme.palette.text.secondary,
      marginBottom: 4,
    },

    '& .value-text': {
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      wordBreak: 'break-word',

      '&.empty': {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
      },
    },

    '& .selected-indicator': {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 18,
      height: 18,
      borderRadius: '50%',
      background: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '0.6875rem',
      fontWeight: 700,
    },
  };
});

const StyledStrategyButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

interface StyledStrategyButtonProps {
  $isDark: boolean;
  $selected: boolean;
  $accentColor?: string;
}

const StyledStrategyButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledStrategyButtonProps>(({ theme, $isDark, $selected, $accentColor }) => {
  const color = $accentColor || baseColors.primary[$isDark ? 400 : 500];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${$selected ? alpha(color, 0.4) : $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    background: $selected ? alpha(color, 0.12) : 'transparent',
    color: $selected ? color : theme.palette.text.secondary,
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,

    '&:hover': {
      background: alpha(color, 0.08),
      borderColor: alpha(color, 0.25),
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    '& .strategy-icon': {
      fontSize: '0.875rem',
    },
  };
});

const StyledCustomInput = styled(motion.div)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const StyledResultPreview = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $accentColor?: string;
}>(({ theme, $isDark, $accentColor }) => {
  const color = $accentColor || baseColors.success[$isDark ? 400 : 500];

  return {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    background: alpha(color, 0.08),
    border: `1px solid ${alpha(color, 0.2)}`,

    '& .result-label': {
      fontSize: '0.6875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: color,
      marginBottom: 4,
    },

    '& .result-value': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const MergeStrategySelector = forwardRef<HTMLDivElement, MergeStrategySelectorProps>(
  (
    { field, primaryValue, secondaryValue, strategy, customValue, onStrategyChange, disabled = false, neonAccent },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [localCustomValue, setLocalCustomValue] = useState<string>(
      customValue !== undefined ? String(customValue) : '',
    );

    // Get accent color
    const accentColor = neonAccent ? getNeonStyles(neonAccent, isDark).base : undefined;

    // Format values for display
    const formattedPrimary = formatValue(primaryValue, field.fieldType);
    const formattedSecondary = formatValue(secondaryValue, field.fieldType);

    // Compute result value based on strategy
    const getResultValue = useCallback(() => {
      switch (strategy) {
        case 'KEEP_PRIMARY':
          return formattedPrimary;
        case 'KEEP_SECONDARY':
          return formattedSecondary;
        case 'MERGE':
          // For text fields, concatenate
          if (formattedPrimary === '-') return formattedSecondary;
          if (formattedSecondary === '-') return formattedPrimary;
          if (formattedPrimary === formattedSecondary) return formattedPrimary;
          return `${formattedPrimary} / ${formattedSecondary}`;
        case 'CUSTOM':
          return localCustomValue || '-';
        default:
          return formattedPrimary;
      }
    }, [strategy, formattedPrimary, formattedSecondary, localCustomValue]);

    // Handlers
    const handleValueCardClick = useCallback(
      (side: 'primary' | 'secondary') => {
        if (disabled) return;
        const newStrategy: MergeStrategyType = side === 'primary' ? 'KEEP_PRIMARY' : 'KEEP_SECONDARY';
        onStrategyChange(newStrategy);
      },
      [onStrategyChange, disabled],
    );

    const handleStrategyClick = useCallback(
      (newStrategy: MergeStrategyType) => {
        if (disabled) return;
        onStrategyChange(newStrategy, newStrategy === 'CUSTOM' ? localCustomValue : undefined);
      },
      [onStrategyChange, localCustomValue, disabled],
    );

    const handleCustomValueChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setLocalCustomValue(value);
        if (strategy === 'CUSTOM') {
          onStrategyChange('CUSTOM', value);
        }
      },
      [onStrategyChange, strategy],
    );

    return (
      <StyledContainer ref={ref} $isDark={isDark}>
        {/* Field Header */}
        <StyledFieldHeader>
          <Typography className="field-label">{field.label}</Typography>
          <Typography className="field-type">{field.fieldType}</Typography>
        </StyledFieldHeader>

        {/* Value Cards */}
        <StyledValuesRow>
          <StyledValueCard
            $isDark={isDark}
            $selected={strategy === 'KEEP_PRIMARY'}
            $side="primary"
            $accentColor={accentColor}
            onClick={() => handleValueCardClick('primary')}
            disabled={disabled}
          >
            <span className="value-label">Primary</span>
            <span className={`value-text ${formattedPrimary === '-' ? 'empty' : ''}`}>{formattedPrimary}</span>
            {strategy === 'KEEP_PRIMARY' && <span className="selected-indicator">{'\u2713'}</span>}
          </StyledValueCard>

          <StyledValueCard
            $isDark={isDark}
            $selected={strategy === 'KEEP_SECONDARY'}
            $side="secondary"
            $accentColor={accentColor}
            onClick={() => handleValueCardClick('secondary')}
            disabled={disabled}
          >
            <span className="value-label">Secondary</span>
            <span className={`value-text ${formattedSecondary === '-' ? 'empty' : ''}`}>{formattedSecondary}</span>
            {strategy === 'KEEP_SECONDARY' && <span className="selected-indicator">{'\u2713'}</span>}
          </StyledValueCard>
        </StyledValuesRow>

        {/* Strategy Buttons */}
        <StyledStrategyButtons>
          {STRATEGY_OPTIONS.map((option) => (
            <GlassTooltip key={option.value} title={option.label} arrow>
              <StyledStrategyButton
                $isDark={isDark}
                $selected={strategy === option.value}
                $accentColor={accentColor}
                onClick={() => handleStrategyClick(option.value)}
                disabled={disabled}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="strategy-icon">{option.icon}</span>
                {option.label}
              </StyledStrategyButton>
            </GlassTooltip>
          ))}
        </StyledStrategyButtons>

        {/* Custom Value Input */}
        <AnimatePresence>
          {strategy === 'CUSTOM' && (
            <StyledCustomInput
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Enter custom value..."
                value={localCustomValue}
                onChange={handleCustomValueChange}
                disabled={disabled}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.875rem',
                  },
                }}
              />
            </StyledCustomInput>
          )}
        </AnimatePresence>

        {/* Result Preview */}
        <StyledResultPreview $isDark={isDark} $accentColor={accentColor}>
          <Typography className="result-label">Result Value</Typography>
          <Typography className="result-value">{getResultValue()}</Typography>
        </StyledResultPreview>
      </StyledContainer>
    );
  },
);

MergeStrategySelector.displayName = 'MergeStrategySelector';

export default MergeStrategySelector;
